import * as React from "react";
import { useLayoutEffect, useRef, useState } from "react";
import { SuggestionCategory, WelcomeConfig } from "../../types";
import { join } from "../../utils/classNames";
import styles from "./Welcome.module.css";

interface Props {
	welcome?: WelcomeConfig;
	suggestions?: SuggestionCategory[];
	onPick: (text: string) => void;
}

export const Welcome: React.FC<Props> = ({ welcome, suggestions, onPick }) => {
	const categories = (suggestions || []).filter((c) => c.questions.length > 0);
	const [activeIndex, setActiveIndex] = useState<number | null>(null);
	const [closing, setClosing] = useState(false);
	const active = activeIndex != null ? categories[activeIndex] : undefined;

	const open = (i: number) => {
		setClosing(false);
		setActiveIndex(i);
	};
	const close = () => setClosing(true);

	const scrollRef = useRef<HTMLDivElement>(null);
	const [shadow, setShadow] = useState({ top: false, bottom: false });

	const updateShadow = () => {
		const el = scrollRef.current;
		if (!el) return;
		const top = el.scrollTop > 0;
		const bottom = el.scrollTop + el.clientHeight < el.scrollHeight - 1;
		setShadow((s) => (s.top === top && s.bottom === bottom ? s : { top, bottom }));
	};

	// Recompute when the open category changes (different question count).
	useLayoutEffect(updateShadow, [activeIndex]);

	return (
		<div className={styles.base}>
			{welcome?.iconUrl && (
				<img src={welcome.iconUrl} alt="" width={48} height={48} className={styles.orb} loading="lazy" />
			)}
			{welcome?.title && <div className={styles.title}>{welcome.title}</div>}
			{welcome?.subtitle && <div className={styles.subtitle}>{welcome.subtitle}</div>}
			{categories.length > 0 && (
				<div className={styles.suggestArea}>
					<div className={styles.pills}>
						{categories.map((c, i) => (
							<button
								key={c.label}
								type="button"
								className={styles.pill}
								aria-pressed={i === activeIndex && !closing}
								onClick={() => (i === activeIndex && !closing ? close() : open(i))}
							>
								{c.label}
							</button>
						))}
					</div>
					{active && (
						<div
							className={join(styles.panel, closing && styles.panelClosing)}
							role="dialog"
							aria-label={active.label}
							onAnimationEnd={() => {
								if (closing) {
									setActiveIndex(null);
									setClosing(false);
								}
							}}
						>
							<div className={styles.panelHeader}>
								<span className={styles.panelTitle}>{active.label}</span>
								<button
									type="button"
									className={styles.panelClose}
									onClick={close}
									aria-label="Close suggestions"
								>
									×
								</button>
							</div>
							<div className={styles.panelBody}>
								<div ref={scrollRef} className={styles.panelQuestions} onScroll={updateShadow}>
									{active.questions.map((q) => (
										<button
											key={q}
											className={styles.suggestion}
											onClick={() => onPick(q)}
											type="button"
										>
											{q}
										</button>
									))}
								</div>
								<div className={join(styles.shadowTop, shadow.top && styles.shadowVisible)} aria-hidden />
								<div
									className={join(styles.shadowBottom, shadow.bottom && styles.shadowVisible)}
									aria-hidden
								/>
							</div>
						</div>
					)}
				</div>
			)}
		</div>
	);
};
